import axios from 'axios';
import type ReconnectingWebSocket from 'reconnecting-websocket';

import { IOrderbook, ITickerSocket, TradeSocket } from 'types/cex/spot';
import { IChartTimeframe, KLine, Trade } from 'types/charts';
import { normalizeNumber } from 'utils/formats/normalizeNumber';

import { BaseStrategy, IInitArgs } from '../base_strategy';
import { aggregateOrderbook } from '../utils';

const DEXTRADE_REST_BASE = 'https://api.dex-trade.com';
const DEXTRADE_GRAPH_BASE = 'https://socket.dex-trade.com';
const MARKET_POLL_INTERVAL_MS = 3_000;
const KLINE_POLL_INTERVAL_MS = 15_000;
const MAX_GRAPH_LIMIT = 3_000;

type DexTradeEnvelope<T> = {
  status: boolean;
  data: T;
  error?: string;
  message?: string;
};

type DexTradeSymbol = {
  id: number;
  pair: string;
  base: string;
  quote: string;
  rate_decimal: number;
  base_decimal: number;
  quote_decimal: number;
};

type DexTradeTicker = {
  pair: string;
  last: string | number;
  high: string | number;
  low: string | number;
  volume_24H: string | number;
  percent_change?: string | number;
  percent_сhange?: string | number;
};

type DexTradeBookLevel = {
  volume: string | number;
  rate: string | number;
  count: number;
};

type DexTradeBook = {
  sequenceId: number;
  buy: DexTradeBookLevel[];
  sell: DexTradeBookLevel[];
};

type DexTradePublicTrade = {
  volume: string | number;
  rate: string | number;
  price: string | number;
  timestamp: number;
  type: string;
};

export type DexTradeHistoryRow = {
  low: string | number;
  high: string | number;
  volume: string | number;
  time: number;
  open: string | number;
  close: string | number;
  pair_id?: number;
  pair?: string;
};

const unwrap = <T>(response: DexTradeEnvelope<T>, resource: string): T => {
  if (!response?.status) {
    throw new Error(response?.error || response?.message || `Dex-Trade ${resource} request failed`);
  }
  return response.data;
};

const scaleGraphNumber = (value: string | number, decimals: number): string =>
  normalizeNumber(Number(value) / 10 ** decimals);

export const dexTradePrecisionFromDecimals = (decimals: number): number => {
  if (!Number.isInteger(decimals) || decimals < 0 || decimals > 18) return 1;
  return decimals;
};

export const normalizeDexTradeHistory = (
  rows: DexTradeHistoryRow[],
  priceDecimals: number,
  baseDecimals: number,
): KLine[] =>
  rows
    .filter(row => Number.isFinite(row.time) && row.time > 0)
    .map(
      row =>
        [
          row.time * 1_000,
          scaleGraphNumber(row.open, priceDecimals),
          scaleGraphNumber(row.high, priceDecimals),
          scaleGraphNumber(row.low, priceDecimals),
          scaleGraphNumber(row.close, priceDecimals),
          scaleGraphNumber(row.volume, baseDecimals),
        ] as KLine,
    )
    .sort((left, right) => left[0] - right[0]);

export const aggregateDexTradeHistory = (rows: KLine[], intervalMs: number): KLine[] => {
  const result: KLine[] = [];

  rows.forEach(row => {
    const bucket = Math.floor(row[0] / intervalMs) * intervalMs;
    const current = result.at(-1);

    if (!current || current[0] !== bucket) {
      result.push([bucket, row[1], row[2], row[3], row[4], row[5]]);
      return;
    }

    current[2] = normalizeNumber(Math.max(Number(current[2]), Number(row[2])));
    current[3] = normalizeNumber(Math.min(Number(current[3]), Number(row[3])));
    current[4] = row[4];
    current[5] = normalizeNumber(Number(current[5]) + Number(row[5]));
  });

  return result;
};

const timeframeMinutes: Record<IChartTimeframe, number> = {
  '1s': 1,
  '10s': 1,
  '30s': 1,
  '1m': 1,
  '3m': 3,
  '5m': 5,
  '15m': 15,
  '30m': 30,
  '1h': 60,
  '2h': 120,
  '4h': 240,
  '6h': 360,
  '8h': 480,
  '12h': 720,
  '24h': 1_440,
  '1d': 1_440,
  '2d': 2_880,
  '3d': 4_320,
  '5d': 7_200,
  '168h': 10_080,
  '1w': 10_080,
  '1M': 43_200,
};

const nativeGraphResolutions = new Set([1, 5, 15, 30, 60]);

const historyPlan = (interval: IChartTimeframe, limit: number) => {
  const targetMinutes = timeframeMinutes[interval] ?? 1;
  const resolution = nativeGraphResolutions.has(targetMinutes)
    ? targetMinutes
    : targetMinutes < 60
      ? 1
      : 60;
  const aggregationFactor = Math.max(1, Math.ceil(targetMinutes / resolution));

  return {
    resolution,
    targetIntervalMs: targetMinutes * 60_000,
    requestLimit: Math.min(MAX_GRAPH_LIMIT, Math.max(5, limit * aggregationFactor)),
  };
};

const intervalMsToTimeframe = (interval: number): IChartTimeframe =>
  (
    ({
      60_000: '1m',
      180_000: '3m',
      300_000: '5m',
      900_000: '15m',
      1_800_000: '30m',
      3_600_000: '1h',
      7_200_000: '2h',
      14_400_000: '4h',
      21_600_000: '6h',
      28_800_000: '8h',
      43_200_000: '12h',
      86_400_000: '1d',
      604_800_000: '1w',
    }) as Partial<Record<number, IChartTimeframe>>
  )[interval] ?? '1m';

export class DexTradeStrategy implements BaseStrategy {
  ws: ReconnectingWebSocket | undefined;
  pair: string;
  depth: number;
  precision = 1;
  selectedPrecision = 1;
  bids: [string, string][] = [];
  asks: [string, string][] = [];
  metadata: DexTradeSymbol | undefined;
  metadataPromise: Promise<DexTradeSymbol> | undefined;
  marketPollInterval: ReturnType<typeof setInterval> | undefined;
  klinePollInterval: ReturnType<typeof setInterval> | undefined;
  seenTradeKeys = new Set<string>();

  onOrderbookUpdate: (v: IOrderbook) => void;
  onTickerUpdate: (v: ITickerSocket) => void;
  klineUpdate: (v: KLine) => void;
  precisionUpdate: (v: number) => void;
  onTradeUpdate: (v: TradeSocket) => void;

  constructor({
    base,
    quote,
    orderbookDepth,
    onOrderbookUpdate,
    onTickerUpdate,
    klineUpdate,
    precisionUpdate,
    onTradeUpdate,
  }: IInitArgs) {
    this.pair = `${base.toUpperCase()}${quote.toUpperCase()}`;
    this.depth = orderbookDepth ?? 20;
    this.onOrderbookUpdate = onOrderbookUpdate;
    this.onTickerUpdate = onTickerUpdate;
    this.klineUpdate = klineUpdate;
    this.precisionUpdate = precisionUpdate;
    this.onTradeUpdate = onTradeUpdate;
  }

  loadMetadata = async (): Promise<DexTradeSymbol> => {
    if (this.metadata) return this.metadata;
    if (this.metadataPromise) return this.metadataPromise;

    this.metadataPromise = axios
      .get<DexTradeEnvelope<DexTradeSymbol[]>>(`${DEXTRADE_REST_BASE}/v1/public/symbols`)
      .then(response => {
        const symbols = unwrap(response.data, 'symbols');
        const metadata = symbols.find(item => item.pair.toUpperCase() === this.pair);
        if (!metadata) throw new Error(`Dex-Trade symbol ${this.pair} was not found`);
        this.metadata = metadata;
        return metadata;
      })
      .catch(error => {
        this.metadataPromise = undefined;
        throw error;
      });

    return this.metadataPromise;
  };

  fetchTickerPrecision = async () => {
    try {
      const metadata = await this.loadMetadata();
      const precision = dexTradePrecisionFromDecimals(metadata.rate_decimal);
      this.precision = precision;
      this.selectedPrecision = precision;
      this.precisionUpdate(precision);
    } catch (error) {
      console.log('Dex-Trade precision error:', error);
    }
  };

  fetchOrderbook = async () => {
    try {
      const response = await axios.get<DexTradeEnvelope<DexTradeBook>>(
        `${DEXTRADE_REST_BASE}/v1/public/book`,
        { params: { pair: this.pair } },
      );
      const book = unwrap(response.data, 'orderbook');

      this.asks = (book.sell ?? [])
        .filter(level => level.count > 0 && Number(level.rate) > 0 && Number(level.volume) > 0)
        .map(level => [String(level.rate), String(level.volume)] as [string, string])
        .sort((left, right) => Number(left[0]) - Number(right[0]));
      this.bids = (book.buy ?? [])
        .filter(level => level.count > 0 && Number(level.rate) > 0 && Number(level.volume) > 0)
        .map(level => [String(level.rate), String(level.volume)] as [string, string])
        .sort((left, right) => Number(right[0]) - Number(left[0]));

      this.publishOrderbook();
    } catch (error) {
      console.log('Dex-Trade orderbook error:', error);
    }
  };

  fetchTicker = async () => {
    try {
      const response = await axios.get<DexTradeEnvelope<DexTradeTicker>>(
        `${DEXTRADE_REST_BASE}/v1/public/ticker`,
        { params: { pair: this.pair } },
      );
      const ticker = unwrap(response.data, 'ticker');
      const last = String(ticker.last);
      const baseVolume = String(ticker.volume_24H);

      this.onTickerUpdate({
        baseVolume,
        change: normalizeNumber(ticker.percent_сhange ?? ticker.percent_change ?? 0),
        close: last,
        high: String(ticker.high),
        last,
        low: String(ticker.low),
        quoteVolume: normalizeNumber(Number(baseVolume) * Number(last)),
      });
    } catch (error) {
      console.log('Dex-Trade ticker error:', error);
    }
  };

  fetchKlines = async ({
    from,
    to,
    limit,
    interval,
  }: {
    base: string;
    quote: string;
    from: number;
    to: number;
    limit: number;
    interval: IChartTimeframe;
  }): Promise<KLine[]> => {
    try {
      const metadata = await this.loadMetadata();
      const plan = historyPlan(interval, limit);
      const response = await axios.get<DexTradeHistoryRow[]>(`${DEXTRADE_GRAPH_BASE}/graph/hist`, {
        params: {
          t: this.pair,
          r: plan.resolution,
          end: Math.floor(to / 1_000),
          limit: plan.requestLimit,
        },
      });
      const pairRows = response.data.filter(
        row =>
          (!row.pair || row.pair.toUpperCase() === this.pair) &&
          (!row.pair_id || row.pair_id === metadata.id),
      );
      const normalized = normalizeDexTradeHistory(
        pairRows,
        metadata.rate_decimal,
        metadata.base_decimal,
      );
      const aggregated = aggregateDexTradeHistory(normalized, plan.targetIntervalMs);
      const fromMs = from < 10_000_000_000 ? from * 1_000 : from;
      const toMs = to < 10_000_000_000 ? to * 1_000 : to;
      const firstBucket = Math.floor(fromMs / plan.targetIntervalMs) * plan.targetIntervalMs;

      return aggregated
        .filter(row => row[0] >= firstBucket && row[0] < toMs)
        .slice(-Math.max(1, limit));
    } catch (error) {
      console.log('Dex-Trade klines error:', error);
      return [];
    }
  };

  fetchTrades = async ({
    limit,
  }: {
    base: string;
    quote: string;
    limit: number;
  }): Promise<Trade[]> => {
    try {
      const response = await axios.get<DexTradeEnvelope<DexTradePublicTrade[]>>(
        `${DEXTRADE_REST_BASE}/v1/public/trades`,
        { params: { pair: this.pair } },
      );
      const trades = unwrap(response.data, 'public trades')
        .filter(item => item.timestamp > 0 && Number(item.rate) > 0 && Number(item.volume) > 0)
        .map(item => ({
          price: String(item.rate),
          quantity: String(item.volume),
          timestamp: String(item.timestamp * 1_000),
          side: item.type.toUpperCase() === 'SELL' ? 'sell' : 'buy',
        }))
        .sort((left, right) => Number(right.timestamp) - Number(left.timestamp));

      return trades.slice(0, limit);
    } catch (error) {
      console.log('Dex-Trade trades error:', error);
      return [];
    }
  };

  pollTrades = async () => {
    const trades = await this.fetchTrades({ base: '', quote: '', limit: 100 });
    const unseen = trades.filter(trade => {
      const key = `${trade.timestamp}|${trade.price}|${trade.quantity}|${trade.side}`;
      if (this.seenTradeKeys.has(key)) return false;
      this.seenTradeKeys.add(key);
      return true;
    });

    unseen.reverse().forEach(trade => this.onTradeUpdate(trade));
    if (this.seenTradeKeys.size > 500) {
      const recentKeys = trades.map(
        trade => `${trade.timestamp}|${trade.price}|${trade.quantity}|${trade.side}`,
      );
      this.seenTradeKeys = new Set(recentKeys);
    }
  };

  pollKline = async (intervalMs: number) => {
    const interval = intervalMsToTimeframe(intervalMs);
    const now = Date.now();
    const targetMs = timeframeMinutes[interval] * 60_000;
    const rows = await this.fetchKlines({
      base: '',
      quote: '',
      from: now - targetMs * 3,
      to: now + 1_000,
      limit: 3,
      interval,
    });
    const latest = rows.at(-1);
    if (latest) this.klineUpdate(latest);
  };

  connectWebSocket = ({ interval }: { interval: number }) => {
    this.closeWebSocket();

    void Promise.allSettled([this.fetchOrderbook(), this.fetchTicker(), this.pollTrades()]);
    void this.pollKline(interval);

    this.marketPollInterval = setInterval(() => {
      void Promise.allSettled([this.fetchOrderbook(), this.fetchTicker(), this.pollTrades()]);
    }, MARKET_POLL_INTERVAL_MS);
    this.klinePollInterval = setInterval(() => {
      void this.pollKline(interval);
    }, KLINE_POLL_INTERVAL_MS);
  };

  closeWebSocket = () => {
    if (this.marketPollInterval) {
      clearInterval(this.marketPollInterval);
      this.marketPollInterval = undefined;
    }
    if (this.klinePollInterval) {
      clearInterval(this.klinePollInterval);
      this.klinePollInterval = undefined;
    }
    this.ws = undefined;
  };

  publishOrderbook = () => {
    this.onOrderbookUpdate(
      aggregateOrderbook(this.asks, this.bids, this.selectedPrecision, this.depth),
    );
  };

  switchOrderbookTick = ({ tick }: { tick: number }) => {
    this.selectedPrecision = tick;
    this.publishOrderbook();
  };

  setOrderbookDepth = (depth: number) => {
    this.depth = depth;
    this.publishOrderbook();
  };

  getOrderbookTicks = (precision: number) =>
    Array.from({ length: precision })
      .fill(0)
      .map((_, index) => index + 1)
      .map(value => ({ value: normalizeNumber(1 / 10 ** value), tick: value, id: value }))
      .slice(-3);

  fetch = async () => {
    await this.fetchTickerPrecision();
    await Promise.allSettled([this.fetchOrderbook(), this.fetchTicker()]);
  };

  static init = async (args: IInitArgs) => {
    const instance = new DexTradeStrategy(args);
    await instance.fetch();
    return instance;
  };
}
